import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  FileText,
  Package,
  Clock,
  User,
  Undo2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TicketDetail() {
  const { ticketId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [labNotes, setLabNotes] = useState('');
  const [accountsNotes, setAccountsNotes] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchTicket = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTicket(data);
        setEditData({
          batch_number: data.batch_number,
          product_name: data.product_name,
          quantity: data.quantity,
          unit: data.unit,
          production_date: data.production_date
        });
      } else if (response.status === 404) {
        toast.error('Ticket not found');
        navigate('/tickets');
      }
    } catch (error) {
      console.error('Failed to fetch ticket:', error);
      toast.error('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [ticketId, navigate]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchTicket();
    }
  }, [authLoading, user, fetchTicket]);

  const handleUpdate = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        toast.success('Ticket updated');
        setEditing(false);
        fetchTicket();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Update failed');
      }
    } catch (error) {
      toast.error('Update failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Ticket deleted');
        navigate('/tickets');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Delete failed');
      }
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      setActionLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleLabApproval = async (status) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}/lab-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status, notes: labNotes })
      });

      if (response.ok) {
        toast.success(`Ticket ${status}`);
        fetchTicket();
        setLabNotes('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccountsUpdate = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}/accounts-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: accountsNotes })
      });

      if (response.ok) {
        toast.success('Stock entered - Ticket closed');
        fetchTicket();
        setAccountsNotes('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLabRevert = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}/lab-revert`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Lab decision reverted');
        fetchTicket();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Revert failed');
      }
    } catch (error) {
      toast.error('Revert failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccountsRevert = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}/accounts-revert`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Stock entry reverted');
        fetchTicket();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Revert failed');
      }
    } catch (error) {
      toast.error('Revert failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status, labStatus) => {
    if (status === 'closed' && labStatus === 'rejected') {
      return <span className="status-badge status-rejected">Rejected</span>;
    }
    
    const statusMap = {
      pending_lab: { class: 'status-pending-lab', label: 'Pending Lab' },
      pending_accounts: { class: 'status-pending-accounts', label: 'Pending Accounts' },
      closed: { class: 'status-closed', label: 'Closed' }
    };
    
    const statusInfo = statusMap[status] || { class: 'status-pending-lab', label: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const canEdit = (user?.role === 'production' || user?.role === 'manager') && ticket?.status === 'pending_lab';
  const canDelete = user?.effective_permissions?.includes('edit_tickets') || false;
  const canLabApprove = (user?.role === 'lab' || user?.role === 'manager') && ticket?.status === 'pending_lab';
  const canAccountsUpdate = (user?.role === 'accounts' || user?.role === 'manager') && ticket?.status === 'pending_accounts';
  
  // Revert permissions
  const canLabRevert = (user?.role === 'lab' || user?.role === 'manager') && 
    ((ticket?.status === 'pending_accounts' && ticket?.lab_status === 'approved') ||
     (ticket?.status === 'closed' && ticket?.lab_status === 'rejected'));
  const canAccountsRevert = (user?.role === 'accounts' || user?.role === 'manager') && 
    ticket?.status === 'closed' && ticket?.accounts_status === 'stock_entered';

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  if (!ticket) {
    return (
      <Layout>
        <div className="page-content">
          <div className="empty-state">
            <h3>Ticket not found</h3>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="ticket-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/tickets')}
              className="h-10 w-10 p-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900 font-mono">{ticket.ticket_id}</h1>
                {getStatusBadge(ticket.status, ticket.lab_status)}
              </div>
              <p className="text-slate-500 mt-1">Created {new Date(ticket.created_at).toLocaleString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {canEdit && !editing && (
              <Button
                variant="outline"
                onClick={() => setEditing(true)}
                className="h-12"
                data-testid="edit-btn"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {canDelete && (
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(true)}
                className="h-12 text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ticket Details Card */}
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="ticket-info">
              <div className="flex items-center gap-2 mb-6">
                <FileText className="w-5 h-5 text-slate-700" />
                <h2 className="text-lg font-semibold text-slate-900">Ticket Details</h2>
              </div>

              {editing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Batch Number</label>
                      <Input
                        value={editData.batch_number}
                        onChange={(e) => setEditData({ ...editData, batch_number: e.target.value })}
                        className="h-12"
                        data-testid="edit-batch-number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Product Name</label>
                      <Input
                        value={editData.product_name}
                        onChange={(e) => setEditData({ ...editData, product_name: e.target.value })}
                        className="h-12"
                        data-testid="edit-product-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Quantity</label>
                      <Input
                        type="number"
                        value={editData.quantity}
                        onChange={(e) => setEditData({ ...editData, quantity: parseFloat(e.target.value) })}
                        className="h-12"
                        data-testid="edit-quantity"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Unit</label>
                      <Input
                        value={editData.unit}
                        onChange={(e) => setEditData({ ...editData, unit: e.target.value })}
                        className="h-12"
                        data-testid="edit-unit"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Production Date</label>
                      <Input
                        type="date"
                        value={editData.production_date}
                        onChange={(e) => setEditData({ ...editData, production_date: e.target.value })}
                        className="h-12"
                        data-testid="edit-production-date"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-4">
                    <Button
                      onClick={handleUpdate}
                      disabled={actionLoading}
                      className="h-12 px-6 bg-slate-900 hover:bg-slate-800"
                      data-testid="save-btn"
                    >
                      {actionLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditing(false)}
                      className="h-12"
                      data-testid="cancel-edit-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Batch Number</span>
                    <p className="mt-1 text-base font-medium text-slate-900">{ticket.batch_number}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Product Name</span>
                    <p className="mt-1 text-base font-medium text-slate-900">{ticket.product_name}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Quantity</span>
                    <p className="mt-1 text-base font-medium text-slate-900">{ticket.quantity} {ticket.unit}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Production Date</span>
                    <p className="mt-1 text-base font-medium text-slate-900">
                      {new Date(ticket.production_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</span>
                    <div className="mt-1">{getStatusBadge(ticket.status, ticket.lab_status)}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Lab Approval Section */}
            {canLabApprove && (
              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="lab-approval-section">
                <div className="flex items-center gap-2 mb-6">
                  <Package className="w-5 h-5 text-amber-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Lab Approval</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Notes (Optional)</label>
                    <Textarea
                      value={labNotes}
                      onChange={(e) => setLabNotes(e.target.value)}
                      placeholder="Add any notes about the lab test results..."
                      className="min-h-[100px]"
                      data-testid="lab-notes"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => handleLabApproval('approved')}
                      disabled={actionLoading}
                      className="h-12 px-6 bg-green-600 hover:bg-green-700"
                      data-testid="approve-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleLabApproval('rejected')}
                      disabled={actionLoading}
                      className="h-12 px-6 bg-red-600 hover:bg-red-700"
                      data-testid="reject-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Accounts Update Section */}
            {canAccountsUpdate && (
              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="accounts-update-section">
                <div className="flex items-center gap-2 mb-6">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Accounts Processing</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Once the stock is entered in Tally, click below to close this ticket.
                  </p>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Notes (Optional)</label>
                    <Textarea
                      value={accountsNotes}
                      onChange={(e) => setAccountsNotes(e.target.value)}
                      placeholder="Add any notes about the stock entry..."
                      className="min-h-[100px]"
                      data-testid="accounts-notes"
                    />
                  </div>
                  <Button
                    onClick={handleAccountsUpdate}
                    disabled={actionLoading}
                    className="h-12 px-6 bg-green-600 hover:bg-green-700"
                    data-testid="stock-entered-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {actionLoading ? 'Processing...' : 'Stock Entered in Tally'}
                  </Button>
                </div>
              </div>
            )}

            {/* Lab Revert Section */}
            {canLabRevert && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6" data-testid="lab-revert-section">
                <div className="flex items-center gap-2 mb-4">
                  <Undo2 className="w-5 h-5 text-amber-600" />
                  <h2 className="text-lg font-semibold text-amber-900">Revert Lab Decision</h2>
                </div>
                <p className="text-sm text-amber-700 mb-4">
                  Made a mistake? Click below to revert the lab {ticket?.lab_status === 'approved' ? 'approval' : 'rejection'} and move the ticket back to Pending Lab.
                </p>
                <Button
                  onClick={handleLabRevert}
                  disabled={actionLoading}
                  variant="outline"
                  className="h-12 px-6 border-amber-400 text-amber-700 hover:bg-amber-100"
                  data-testid="lab-revert-btn"
                >
                  <Undo2 className="w-4 h-4 mr-2" />
                  {actionLoading ? 'Reverting...' : 'Revert Lab Decision'}
                </Button>
              </div>
            )}

            {/* Accounts Revert Section */}
            {canAccountsRevert && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6" data-testid="accounts-revert-section">
                <div className="flex items-center gap-2 mb-4">
                  <Undo2 className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-blue-900">Revert Stock Entry</h2>
                </div>
                <p className="text-sm text-blue-700 mb-4">
                  Made a mistake? Click below to revert the stock entry and move the ticket back to Pending Accounts.
                </p>
                <Button
                  onClick={handleAccountsRevert}
                  disabled={actionLoading}
                  variant="outline"
                  className="h-12 px-6 border-blue-400 text-blue-700 hover:bg-blue-100"
                  data-testid="accounts-revert-btn"
                >
                  <Undo2 className="w-4 h-4 mr-2" />
                  {actionLoading ? 'Reverting...' : 'Revert Stock Entry'}
                </Button>
              </div>
            )}
          </div>

          {/* Sidebar - Timeline */}
          <div className="space-y-6">
            {/* Status Timeline */}
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="status-timeline">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Status Timeline</h3>
              <div className="space-y-4">
                {/* Created */}
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Created</p>
                    <p className="text-xs text-slate-500">{new Date(ticket.created_at).toLocaleString()}</p>
                  </div>
                </div>

                {/* Lab Approval */}
                {ticket.lab_approved_at && (
                  <div className="flex gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      ticket.lab_status === 'approved' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {ticket.lab_status === 'approved' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        Lab {ticket.lab_status === 'approved' ? 'Approved' : 'Rejected'}
                      </p>
                      <p className="text-xs text-slate-500">{new Date(ticket.lab_approved_at).toLocaleString()}</p>
                      {ticket.lab_notes && (
                        <p className="text-xs text-slate-600 mt-1 italic">"{ticket.lab_notes}"</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Accounts Update */}
                {ticket.accounts_updated_at && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        Stock Entered in Tally
                      </p>
                      <p className="text-xs text-slate-500">{new Date(ticket.accounts_updated_at).toLocaleString()}</p>
                      {ticket.accounts_notes && (
                        <p className="text-xs text-slate-600 mt-1 italic">"{ticket.accounts_notes}"</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Closed */}
                {ticket.status === 'closed' && ticket.lab_status !== 'rejected' && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Ticket Closed</p>
                      <p className="text-xs text-slate-500">{new Date(ticket.updated_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Info */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Workflow</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${ticket.status === 'pending_lab' ? 'bg-amber-500' : 'bg-green-500'}`} />
                  <span className={ticket.status === 'pending_lab' ? 'font-medium' : 'text-slate-500'}>Pending Lab</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${ticket.status === 'pending_accounts' ? 'bg-blue-500' : ticket.status === 'closed' ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <span className={ticket.status === 'pending_accounts' ? 'font-medium' : 'text-slate-500'}>Pending Accounts</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${ticket.status === 'closed' ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <span className={ticket.status === 'closed' ? 'font-medium' : 'text-slate-500'}>Closed</span>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            {(ticket.lab_notes || ticket.accounts_notes) && (
              <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="notes-section">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Notes</h4>
                <div className="space-y-4">
                  {ticket.lab_notes && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="w-4 h-4 text-amber-600" />
                        <span className="text-xs font-semibold text-amber-700">Lab Notes</span>
                        {ticket.lab_approved_by && (
                          <span className="text-xs text-amber-600">by {ticket.lab_approved_by}</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700">{ticket.lab_notes}</p>
                      {ticket.lab_approved_at && (
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(ticket.lab_approved_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                  {ticket.accounts_notes && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-semibold text-blue-700">Accounts Notes</span>
                        {ticket.accounts_updated_by && (
                          <span className="text-xs text-blue-600">by {ticket.accounts_updated_by}</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-700">{ticket.accounts_notes}</p>
                      {ticket.accounts_updated_at && (
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(ticket.accounts_updated_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ticket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete ticket {ticket.ticket_id}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
